/**
 * Tenant branding from People `/api/people/api/v1.0/config/branding/` — shared by shell and
 * remote SPAs so suite chrome CSS variables stay consistent.
 */

export interface TenantBranding {
  primary_color: string;
  secondary_color: string;
  /** The sidebar per theme. One colour rarely reads well on both a light page
   *  and a dark one. Blank falls back to `secondary_color`, which is the legacy
   *  single value and is kept in step with the light one server-side. */
  secondary_color_light?: string;
  secondary_color_dark?: string;
  logo_url: string;
  favicon_url: string;
  app_name: string;
  custom_css: string;
}

export const BRANDING_DEFAULTS: TenantBranding = {
  // The product brand, and the fallback an unbranded org gets. Moved from
  // the original yellow by the Liquid Glass rebrand; this is brand-500.
  primary_color: "#3366FF",
  secondary_color: "#000000",
  secondary_color_light: "",
  secondary_color_dark: "",
  logo_url: "",
  favicon_url: "",
  app_name: "",
  custom_css: "",
};

/**
 * Fetch this user's organization branding.
 *
 * Throws rather than returning the WeCo defaults on failure, and that is the
 * whole point. Handing back defaults made every failure look like a successful
 * answer: the caller cached "yellow" as the tenant's real branding, never
 * retried it, and an organization that had chosen its own colour saw WeCo's
 * for the rest of the session — from nothing worse than a token that was not
 * ready yet, or People restarting mid-boot. A throw is retried; a lie is not.
 *
 * Callers still show the defaults meanwhile; see `applyTenantBrandingToDOM`,
 * which is applied with `BRANDING_DEFAULTS` before this is ever awaited.
 */
export async function fetchTenantBranding(
  getToken: () => Promise<string | null>,
): Promise<TenantBranding> {
  const token = await getToken();
  if (!token) throw new Error("tenant branding: not signed in yet");

  const res = await fetch("/api/people/api/v1.0/config/branding/", {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    throw new Error(`tenant branding: HTTP ${res.status}`);
  }
  return res.json();
}

const HEX6_RE = /^#[0-9a-fA-F]{6}$/;
const HEX3_RE = /^#[0-9a-fA-F]{3}$/;

function isValidHexColor(value: string): boolean {
  return HEX6_RE.test(value) || HEX3_RE.test(value);
}

/**
 * Lighten a hex color by mixing it with white.
 * `amount` is 0–1 where 1 = fully white.
 */
function lightenHex(hex: string, amount: number): string {
  if (!isValidHexColor(hex)) return hex;
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h[0]+h[0]+h[1]+h[1]+h[2]+h[2] : h;
  const r = parseInt(full.substring(0, 2), 16);
  const g = parseInt(full.substring(2, 4), 16);
  const b = parseInt(full.substring(4, 6), 16);
  const lr = Math.round(r + (255 - r) * amount);
  const lg = Math.round(g + (255 - g) * amount);
  const lb = Math.round(b + (255 - b) * amount);
  return `#${lr.toString(16).padStart(2, "0")}${lg.toString(16).padStart(2, "0")}${lb.toString(16).padStart(2, "0")}`;
}

/** `#abc` and `#aabbcc` both to [r, g, b]. Assumes `isValidHexColor` passed. */
function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h[0] + h[0] + h[1] + h[1] + h[2] + h[2] : h;
  return [
    parseInt(full.substring(0, 2), 16),
    parseInt(full.substring(2, 4), 16),
    parseInt(full.substring(4, 6), 16),
  ];
}

/** WCAG relative luminance. */
function luminance(hex: string): number {
  const channel = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  const [r, g, b] = hexToRgb(hex);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/**
 * Black or white, whichever is legible on this brand colour.
 *
 * Exported so the White Label previews derive their ink exactly as the running
 * chrome does — a preview computing contrast its own way is a preview that can
 * disagree with the product it is previewing.
 *
 * The default `--color-primary-foreground` is black because the default brand
 * colour is a bright yellow. A tenant who picks a deep blue gets black text on
 * a dark button — a label nobody can read — unless this is derived rather than
 * inherited.
 */
export function readableInk(hex: string): string {
  const l = luminance(hex);
  const onBlack = (l + 0.05) / 0.05;
  const onWhite = 1.05 / (l + 0.05);
  return onBlack >= onWhite ? "#000000" : "#FFFFFF";
}

/** `amount` of `a` blended into `b`, as a hex string. */
function mix(a: string, b: string, amount: number): string {
  const [ar, ag, ab] = hexToRgb(a);
  const [br, bg, bb] = hexToRgb(b);
  const ch = (x: number, y: number) =>
    Math.round(y + (x - y) * amount)
      .toString(16)
      .padStart(2, "0");
  return `#${ch(ar, br)}${ch(ag, bg)}${ch(ab, bb)}`;
}

/** The brand colour at `alpha`, for the tinted washes. */
function rgba(hex: string, alpha: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** XML-escape a single character for embedding in SVG markup. */
function escapeXml(value: string): string {
  return value.replace(/[<>&"']/g, (c) =>
    ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;", "'": "&apos;" })[c] as string,
  );
}

/**
 * A tab icon in the organization's own colours, for tenants that have not
 * uploaded a logo.
 *
 * This is the shipped `favicon.svg` with the colours swapped — the same rounded
 * square and initial the sidebar draws — so an organization that has branded
 * the product does not keep WeCo's own blue mark in the browser tab merely
 * because it had no image to upload. An uploaded favicon or logo still wins.
 */
function brandedFaviconDataUri(brand: string, appName: string): string {
  const initial = escapeXml((appName.trim()[0] || "W").toUpperCase());
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">` +
    `<rect width="32" height="32" rx="6" fill="${brand}"/>` +
    `<text x="16" y="22" text-anchor="middle" ` +
    `font-family="system-ui,-apple-system,sans-serif" font-weight="800" ` +
    `font-size="15" fill="${readableInk(brand)}">${initial}</text></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/** The shell's sidebar — the one surface the secondary colour paints. */
const SIDEBAR = '[data-tour-id="sidebar-root"]';

/**
 * The sidebar rule for one colour.
 *
 * Everything but the background is derived from it, the ink especially: the
 * sidebar's text reads from the theme's own foreground, which is near-black in
 * light mode, so a dark colour behind it would make the navigation disappear.
 */
function sidebarRule(secondary: string, selector: string): string {
  const ink = readableInk(secondary);
  return (
    // Opaque first as a fallback, then the translucent form for browsers with
    // `color-mix`. Painting it flat was what stopped a branded sidebar being
    // glass at all: the Liquid Glass shell puts a drifting colour wash behind
    // every panel, and a solid fill here covered it, so the sidebar read as a
    // white slab beside a translucent workspace.
    //
    // 65% keeps the organization's colour clearly dominant -- it is still
    // their sidebar -- while letting enough of the wash through to match the
    // other panels. The ink below is still derived from the colour at full
    // strength, which stays valid because at 65% the colour, not the pale
    // wash, is what decides whether the composite is light or dark.
    `${selector} { background: ${secondary}; ` +
    `background: color-mix(in srgb, ${secondary} 65%, transparent); ` +
    `--color-foreground: ${ink}; ` +
    // Inside the sidebar, "background" *is* the secondary colour. Without this
    // the "All apps" pill — `bg-foreground text-background` — renders its label
    // in the page's background colour against a pill painted in the derived
    // ink: white on white in light mode.
    `--color-background: ${secondary}; ` +
    `--color-muted-foreground: ${mix(ink, secondary, 0.62)}; ` +
    // `muted` is the hover surface inside the sidebar; it has to lift off the
    // secondary rather than stay the theme's own grey.
    `--color-muted: ${mix(ink, secondary, 0.12)}; ` +
    `--color-border: ${mix(ink, secondary, 0.22)}; }\n`
  );
}

let _brandingStyleEl: HTMLStyleElement | null = null;

function getBrandingStyleEl(): HTMLStyleElement {
  // `isConnected` matters as much as the null check: the cached element can be
  // detached from the document (a hot reload, a host that rebuilds <head>),
  // and writing branding into an orphan is indistinguishable from branding
  // that never applied.
  if (!_brandingStyleEl || !_brandingStyleEl.isConnected) {
    _brandingStyleEl =
      (document.getElementById("tenant-branding-css") as HTMLStyleElement | null) ??
      (() => {
        const s = document.createElement("style");
        s.id = "tenant-branding-css";
        document.head.appendChild(s);
        return s;
      })();
  }
  return _brandingStyleEl;
}

/**
 * Drop any branding this page has applied, restoring the stylesheet's own
 * values.
 *
 * Deliberately not "apply the defaults". The defaults are a single flat colour
 * per variable, while the stylesheet states a light and a dark value for each
 * — writing them back at `:root` would pin one theme's accent over both, and
 * `BRANDING_DEFAULTS.primary_color` is not even the `--color-primary` the
 * shell ships with. Removing the override is the only way to mean "unbranded".
 */
export function resetTenantBrandingInDOM(): void {
  const style = document.getElementById("tenant-branding-css");
  if (style) style.textContent = "";
}

export function applyTenantBrandingToDOM(branding: TenantBranding): void {
  const cssRules: string[] = [];
  const lightCssRules: string[] = [];

  if (branding.primary_color && isValidHexColor(branding.primary_color)) {
    const brand = branding.primary_color;
    cssRules.push(
      // The Tailwind `primary` family. This is the one that matters: the logo
      // tile, every active nav row, the sidebar toggle, the language pill and
      // some three hundred other surfaces are `bg-primary` / `text-primary`,
      // and none of them has ever read `--accent`. Branding a tenant without
      // this wrote four variables almost nothing consumed, which is why an
      // organization could set its colour, watch the editor's own preview
      // change, and still sit inside a yellow shell.
      `--color-primary: ${brand};`,
      `--color-primary-foreground: ${readableInk(brand)};`,
      // The older `--accent` family, still read by the login screen, the UI
      // package's inputs and cards, and the legacy iframed modules.
      `--accent: ${brand};`,
      // Channels, for the many washes composed as rgba(var(--accent-rgb), a).
      `--accent-rgb: ${hexToRgb(brand).join(', ')};`,
      `--accent-hover: ${lightenHex(brand, 0.15)};`,
      `--accent-dim: ${rgba(brand, 0.06)};`,
      `--accent-glow: ${rgba(brand, 0.08)};`,
      `--accent-border: ${rgba(brand, 0.2)};`,
      `--text-accent: ${brand};`,
      `--border-active: ${brand};`,
    );
  }

  // The secondary colour is the sidebar. That is what the White Label editor's
  // own preview has always shown it as — a logo strip and a nav list painted on
  // it — while the only thing it actually set was `--bg-sidebar`, a variable no
  // screen in this shell reads (the sidebar is `bg-muted`). So the field could
  // be changed, previewed, saved, and change nothing at all.
  //
  // Painted through a scoped rule rather than by repointing `--color-muted`,
  // which `bg-muted` resolves from: that token is also every hover state and
  // half the surfaces in every module, so a tenant picking a dark secondary
  // would have repainted the whole product instead of their sidebar.
  // Each theme may name its own sidebar colour, because one that sits well
  // behind the navigation on a white page rarely does on a near-black one.
  // `secondary_color` answers for whichever theme does not override it, so an
  // organization happy with a single colour keeps exactly what it had.
  const forTheme = (override: string | undefined): string =>
    override && isValidHexColor(override) ? override : branding.secondary_color;

  // `#000000` is the server's default for an organization that never chose a
  // sidebar colour (and migration 0055 copied it into both per-theme fields),
  // so it means "unbranded", not "paint it black": an unbranded sidebar
  // follows the theme — light on a light page, dark on a dark one — exactly as
  // it did before the secondary colour painted anything. Each theme is decided
  // on its own, so choosing only a dark colour leaves the light sidebar alone.
  const isChosen = (colour: string): boolean =>
    colour.toLowerCase() !== BRANDING_DEFAULTS.secondary_color.toLowerCase();

  let sidebarCss = "";
  if (branding.secondary_color && isValidHexColor(branding.secondary_color)) {
    const light = forTheme(branding.secondary_color_light);
    const dark = forTheme(branding.secondary_color_dark);

    cssRules.push(`--bg-sidebar: ${dark};`);
    lightCssRules.push(`--bg-sidebar: ${lightenHex(light, 0.92)};`);

    // Each rule is scoped to its own theme via the `.dark` class initTheme
    // puts on the document, so an unchosen theme keeps the stylesheet's own
    // sidebar instead of inheriting the other theme's colour.
    if (isChosen(light)) sidebarCss += sidebarRule(light, `:root:not(.dark) ${SIDEBAR}`);
    if (isChosen(dark)) sidebarCss += sidebarRule(dark, `.dark ${SIDEBAR}`);
  }

  if (cssRules.length > 0 || lightCssRules.length > 0 || sidebarCss) {
    let css = "";
    if (cssRules.length > 0) {
      css += `:root { ${cssRules.join(" ")} }\n`;
    }
    if (lightCssRules.length > 0) {
      css += `[data-theme="light"] { ${lightCssRules.join(" ")} }\n`;
    }
    css += sidebarCss;
    const style = getBrandingStyleEl();
    if (style.textContent !== css) style.textContent = css;
  }

  // The logo stands in when no separate favicon was given. An organization that
  // uploads its logo and then finds WeCo's mark still in the browser tab has
  // reasonably branded the product as far as it was asked to; making them supply
  // the same image twice to finish the job is the tool's problem, not theirs.
  // Falling back once more, to a mark drawn in the tenant's own colour: an
  // organization that has branded everything else should not be left with
  // WeCo's own mark in the tab just because it had no image to upload.
  const brandedIcon =
    branding.primary_color && isValidHexColor(branding.primary_color)
      ? brandedFaviconDataUri(branding.primary_color, branding.app_name || "")
      : "";
  const iconUrl = branding.favicon_url || branding.logo_url || brandedIcon;
  if (iconUrl) {
    const link: HTMLLinkElement =
      document.querySelector('link[rel="icon"]') || document.createElement("link");
    link.rel = "icon";
    link.href = iconUrl;
    if (!link.parentNode) document.head.appendChild(link);
  }

  if (branding.app_name) {
    document.title = `${branding.app_name} — Workspace`;
  }

  if (branding.custom_css) {
    const existing = document.getElementById("tenant-custom-css");
    const style: HTMLStyleElement =
      (existing as HTMLStyleElement | null) ?? document.createElement("style");
    style.id = "tenant-custom-css";
    style.textContent = branding.custom_css;
    if (!style.parentNode) document.head.appendChild(style);
  }
}
